<template>
  <div>
    <base-form :title="contract.title"
               :show-footer="false">
      <template v-slot:content>

        <div class="-mx-3 pb-6 -mt-4">
          <section-separator class="px-4" has-border>
            {{$t('generic.client')}}
          </section-separator>
          <client-card v-if="contract.client"
                       :client="contract.client"
                       class="client-card"
                       type="list">
          </client-card>
        </div>

        <section-separator class="px-4 -mx-4" has-border>
          {{$t('contract.details.deposits')}}
        </section-separator>
        <data-table :data="[contract]"
                    :columns="depositColumns"
                    :searchable="false"
                    :show-pagination="false"
                    :entity="entity"
                    operations=""
                    @on-row-click="onRowClick"
        >
          <template v-slot:valid_till="{row}">
            {{$formatDate(row.valid_till, FRIENDLY_DATETIME_FORMAT)}}
          </template>
          <template v-slot:amount_open="{row}">
            {{row.amount_open}}
          </template>
          <template v-slot:amount_used="{row}">
            {{row.amount_used}}
          </template>
          <template v-slot:amount="{row}">
            {{row.amount}}
          </template>
          <template v-slot:active="{row}">
            <status-badge :status="row.active ? $t('generic.active') : $t('generic.inactive')"/>
          </template>
          <template v-slot:extra-buttons="{row}">
            <tooltip-icon-button icon="far fa-edit"
                                 :content="$t('generic.edit')"
                                 @click.stop="onRowClick(row)">
            </tooltip-icon-button>
          </template>
        </data-table>

        <modal :show.sync="showEditModal" v-if="showEditModal">
          <div slot="header"><h6>{{$t('contract.editContract')}}</h6></div>
          <form @submit.prevent="editContracts">
            <div class="row">
              <div class="col-12">
                <fg-input :placeholder="$t('client.management.contract.validTill')"
                          :name="$t('client.management.contract.validTill')"
                          :label="$t('client.management.contract.validTill')"
                          component="el-date-picker"
                          value-format="yyyy-MM-dd HH:mm:ss"
                          v-model="editModel.valid_till">
                  <small slot="infoBlock">
                    <i class="fas fa-info-circle text-muted"></i>&nbsp;
                    <span v-html="$t('contract.validDateInfo', {total: editModel.amount })"></span>
                  </small>
                </fg-input>
              </div>
              <div class="col-12">
                <fg-input :error="getError($t('client.management.contract.contractName'))"
                          :label="$t('client.management.contract.contractName')"
                          :placeholder="$t('client.management.contract.contractName')"
                          :name="$t('client.management.contract.contractName')"
                          v-model="editModel.title"
                          v-validate="modelValidations.required"
                          required
                >
                </fg-input>
              </div>
              <div class="col-12">
                <fg-input :label="$t('generic.user')"
                          :error="getError($t('generic.user'))"
                          required>
                  <user-autocomplete v-model="user"
                                     v-validate="modelValidations.required"
                                     :search-params="{'match[client_uuid]': contract.client_uuid}"
                                     :multiple="false"
                                     :name="$t('generic.user')"
                                     value-key="user_uuid"
                                     class="w-full"
                  />
                </fg-input>
              </div>
              <div class="col-12">
                <fg-input :placeholder="$t('client.management.contract.amount')"
                          :name="$t('client.management.contract.amount')"
                          :error="getError($t('client.management.contract.amount'))"
                          :label="$t('client.management.contract.amount')"
                          v-validate="modelValidations.amount"
                          type="number"
                          v-model.number="editModel.amount">
                </fg-input>
              </div>
              <div class="col-12">
                <fg-input :label="$t('cms.form.active')">
                  <base-switch v-model="editModel.active"/>
                </fg-input>
              </div>
              <div class="flex w-full justify-center">
                <submit-button native-type="submit"/>
              </div>
            </div>

          </form>
        </modal>

        <section-separator class="px-4 -mx-4" has-border>
          {{$t('contract.details.withdrawals')}}
        </section-separator>

        <data-table :data="contract.contracts || []"
                    :columns="withdrawColumns"
                    :entity="entity"
                    :show-pagination="false"
                    class="mt-4">
          <template v-slot:extra-filter-buttons="{lastQuery}">
            <base-dropdown position="right">
              <base-button slot="title"
                           icon
                           outline
                           type="primary">
                <i class="nc-icon nc-settings-gear-65"></i>
              </base-button>
              <h6 class="dropdown-header">{{$t('generic.settings')}}</h6>
              <div class="dropdown-item" v-clipboard:copy="clipboardWithdrawals"
                   v-clipboard:success="onCopy">
                {{$t('generic.copyToClipboard')}}
              </div>

            </base-dropdown>
          </template>
          <template v-slot:created_at="{row}">
            {{$formatDate(row.created_at, FRIENDLY_DATETIME_FORMAT)}}
          </template>
          <template v-slot:job="{row}">
            <router-link :to="`/jobs/${get(row, 'order.related.data.uuid')}/dashboard`">
              {{get(row, 'order.related.data.title', $t('generic.na'))}}
            </router-link>
          </template>
          <template v-slot:order="{row}">
            <router-link to="/ecommerce/orders">
              {{get(row, 'order.name', $t('generic.na'))}}
            </router-link>
          </template>
        </data-table>

      </template>
    </base-form>
  </div>
</template>
<script>
  import Vue from 'vue'
  import VueClipboard from 'vue-clipboard2'
  import TooltipIconButton from '@/components/Buttons/TooltipIconButton'
  import UserAutocomplete from '@/components/Autocomplete/UserAutocomplete'
  import { DatePicker } from 'element-ui'
  import get from 'lodash/get'
  import ContractsService from 'src/services/ContractService'
  import ClientCard from 'src/components/Cards/ClientCard'
  import entities from 'src/config/entities'
  import { FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'
  import Modal from 'src/components/Modal'

  Vue.use(DatePicker)
  Vue.use(VueClipboard)
  export default {
    name: 'contract-details',
    components:{
      Modal,
      ClientCard,
      TooltipIconButton,
      UserAutocomplete,
    },
    data () {
      return {
        contract: {},
        entity: entities.CONTRACT,
        FRIENDLY_DATETIME_FORMAT,
        showEditModal: false,
        selectedClient: {},
        user: null,
        editModel: {
          id: null,
          valid_till: null,
          active: true,
          amount: 0,
          title: '',
          user_uuid: null,
        }
      }
    },
    computed: {
      id () {
        let { contractId, id } = this.$route.params
        return contractId || id
      },
      modelValidations () {
        return {
          amount: {
            required: true,
            min_value: 0,
            numeric: true
          },
          required: {
            required: true
          }
        }
      },
      depositColumns () {
        return [
          {
            prop: 'valid_till',
            label: this.$t('contract.list.labels.validTill'),
            minWidth: '190px',
            sortable: true,
          },
          {
            prop: 'amount',
            label: this.$t('contract.list.labels.total'),
            minWidth: '100px',
            sortable: true,
          },
          {
            prop: 'amount_used',
            label: this.$t('contract.list.labels.used'),
            minWidth: '100px',
            sortable: true,
          },
          {
            prop: 'amount_open',
            label: this.$t('contract.list.labels.open'),
            minWidth: '100px',
            sortable: true,
          },
          {
            prop: 'active',
            label: this.$t('generic.active'),
            minWidth: '100px',
            sortable: true,
          },
        ]
      },
      withdrawColumns () {
        return [
          {
            prop: 'created_at',
            label: this.$t('generic.createdAt'),
            minWidth: '190px',
            sortable: true,
          },
          {
            prop: 'job',
            label: this.$t('contract.list.labels.job'),
            minWidth: '140px'
          },
          {
            prop: 'order',
            label: this.$t('contract.list.labels.order'),
            minWidth: '140px'
          }
        ]
      },
      clipboardWithdrawals() {
        let contracts = this.contract.contracts || []
        return contracts.map(c => c.title).join('\n')
      }
    },
    methods: {
      get,
      onRowClick (row) {
        this.showEditModal = true
        this.editModel.valid_till = row.valid_till
        this.editModel.id = row.id
        this.editModel.amount = row.amount
        this.editModel.title = row.title
        this.editModel.client_id = row.client_uuid
        this.editModel.active = row.active
        this.user = {
          user_uuid: row.user_uuid,
        }
      },
      async editContracts () {
        this.editModel.id = this.id
        this.editModel.user_uuid = get(this.user, 'user_uuid', '')
        await ContractsService.update(this.id, this.editModel)
        this.showEditModal = false
        await this.refreshData()
      },
      async refreshData() {
        let res = await ContractsService.getById(this.id, {
          with: 'contracts,client,user'
        })
        this.contract = res.data
      },
      onCopy() {
        this.$notify({
          type: 'success',
          message: this.$t('generic.copyToClipboard.success')
        })
      }
    },
    async beforeRouteEnter (to, from, next) {
      try {
        let { contractId, id } = to.params
        let finalId = contractId || id
        let res = await ContractsService.getById(finalId, {
          with: 'contracts,client,user'
        })
        let contract = res.data
        if (contract) {
          next(vm => {
            vm.contract = contract
          })
        } else {
          next(vm => vm.error = 'No data')
        }
      } catch (e) {
        next(vm => vm.error = e)
      }
    }
  }
</script>
<style scoped>
  .client-card {
    margin-top: 10px;
  }
  .client-card /deep/ .card-body {
    padding-left: 0;
  }
</style>
